// ─────────────────────────────────────────────────────────────────────
// personAvatarConfig.js — opzioni e default dell'avatar persona (uomo/donna) di Virtua Cycling
// I livelli (PNG) sono in assets/person/{man,woman}/, vedi personAvatarAssets.js (generato).
// Ordine di sovrapposizione (PersonAvatar.jsx):
//   skin → iris → sopracciglia (colore dei capelli) → lips → maglia (main, accent, trim) → capelli → copricapo → detail → occhiali
// I livelli sono maschere bianche colorate con tintColor; `detail` (contorni/ombre) no.
// Il copricapo (cappellino/casco) è già disegnato insieme al taglio: con il copricapo
// attivo il livello capelli è quello "con copricapo".
// ─────────────────────────────────────────────────────────────────────

// k = chiave di traduzione (person.<k>)
// Tagli "di serie": il default di ogni utente (lib/personAvatar.js defaultPerson) ne sceglie uno con `% length`. NON cambiare ordine e numero di questi:
// cambierebbe l'avatar generato di tutti gli utenti che non l'hanno personalizzato.
export const PERSON_STYLES_BASE = {
  man:   [{ key: 'h1', k: 'hair_short' }, { key: 'h2', k: 'hair_curly' }, { key: 'h3', k: 'hair_wavy' }, { key: 'h4', k: 'hair_shaved' }],
  woman: [{ key: 'h1', k: 'hair_pixie' }, { key: 'h2', k: 'hair_bob' },   { key: 'h3', k: 'hair_curly' }],
}
// Tagli nati per i corridori (assets/person/*/h5..h16, solo senza copricapo): scegliibili anche dagli utenti, ma MAI usati dal default.
const PERSON_STYLES_RIDER = {
  man:   [{ key: 'h5', k: 'hair_wavy_medium' }, { key: 'h6', k: 'hair_receding' }, { key: 'h7', k: 'hair_messy' }, { key: 'h8', k: 'hair_buzz' }, { key: 'h9', k: 'hair_curly_short' },
          { key: 'h10', k: 'hair_side_part' }, { key: 'h11', k: 'hair_slicked_back' }, { key: 'h12', k: 'hair_forward' }, { key: 'h13', k: 'hair_high_line' },
          { key: 'h14', k: 'hair_wavy_short' }, { key: 'h15', k: 'hair_curly_medium' }, { key: 'h16', k: 'hair_receding_short' }],
  woman: [{ key: 'h5', k: 'hair_long' }, { key: 'h6', k: 'hair_ponytail' }, { key: 'h7', k: 'hair_bun' }, { key: 'h8', k: 'hair_long_wavy' }, { key: 'h9', k: 'hair_braid' }, { key: 'h10', k: 'hair_medium' }],
}
// Tagli che l'utente puo' scegliere nell'editor (e che sanitize accetta)
export const PERSON_STYLES = {
  man:   [...PERSON_STYLES_BASE.man, ...PERSON_STYLES_RIDER.man],
  woman: [...PERSON_STYLES_BASE.woman, ...PERSON_STYLES_RIDER.woman],
}
// I tagli h5..h16 non hanno la variante con cappellino o casco: con un copricapo si disegna il taglio di serie del gruppo
// (compromesso deciso il 2026-11-02): corti (anche h14 ondulati corti) -> h1, ricci -> h2 (riccio), mossi e lunghi -> h3.
export const HEADWEAR_FALLBACK = {
  man:   { h5: 'h3', h6: 'h1', h7: 'h1', h8: 'h1', h9: 'h2', h10: 'h1', h11: 'h1', h12: 'h1', h13: 'h1', h14: 'h1', h15: 'h2', h16: 'h1' },
  woman: { h5: 'h2', h6: 'h1', h7: 'h1', h8: 'h2', h9: 'h1', h10: 'h2' },
}

// Barbe (solo uomo): chiavi = livelli beard<N> (la 5 non esiste). Il colore e' quello dei capelli; BEARD_LIGHT = opacita' dell'ombra leggera.
export const PERSON_BEARDS = [
  { key: 1, k: 'beard_stubble' }, { key: 2, k: 'beard_moustache' }, { key: 3, k: 'beard_short' },
  { key: 4, k: 'beard_full' }, { key: 6, k: 'beard_goatee' }, { key: 7, k: 'beard_circle' }, { key: 8, k: 'beard_stubble_light' },
]

// Volti (solo uomo): '' = viso di serie; a..g volti alternativi (chiavi di traduzione person.avatar_face_*). Sopracciglia: 0 normali, 1 sottili, 2 folte
export const PERSON_FACES = [
  { key: '', k: 'avatar_face_none' }, { key: 'a', k: 'avatar_face_a' }, { key: 'b', k: 'avatar_face_b' }, { key: 'c', k: 'avatar_face_c' },
  { key: 'd', k: 'avatar_face_d' }, { key: 'e', k: 'avatar_face_e' }, { key: 'f', k: 'avatar_face_f' }, { key: 'g', k: 'avatar_face_g' },
]
export const PERSON_BROWS = [{ key: 0, k: 'avatar_brow_0' }, { key: 1, k: 'avatar_brow_1' }, { key: 2, k: 'avatar_brow_2' }]
export const BEARD_LIGHT = 0.4

export const PERSON_HEADWEAR = [
  { key: 'none', k: 'wear_none' }, { key: 'cap', k: 'wear_cap' }, { key: 'helmet', k: 'wear_helmet' },
]

// design maglia → zone colorabili (i numeri sono quelli dei file maglia_N)
export const PERSON_JERSEYS = [
  { key: 3, k: 'jersey_classic',    zones: ['main', 'accent', 'trim'] },   // tinta unita se accento = corpo; maniche a contrasto se diverso
  { key: 6, k: 'jersey_vertical',   zones: ['main', 'accent', 'trim'] },
  { key: 2, k: 'jersey_horizontal', zones: ['main', 'accent', 'trim'] },
  { key: 4, k: 'jersey_diagonal',   zones: ['main', 'accent', 'trim'] },
  { key: 7, k: 'jersey_gradient', zones: ['main', 'accent', 'accent2', 'trim'], hidden: true },   // degradé verticale: SOLO kit squadra (personFromKit)
  { key: 8, k: 'jersey_halves',   zones: ['main', 'accent', 'trim'], hidden: true },   // due meta' sinistra/destra: SOLO kit squadra
  { key: 5, k: 'jersey_dots',       zones: ['main', 'accent', 'trim'], hidden: true },   // maglia GPM (Tour: pois rossi su bianco; Vuelta: pois blu): solo per la gara specifica, non scelta libera
]

// Maglie scelte liberamente dall'utente nell'editor (le `hidden` restano negli asset ma non compaiono)
export const PERSON_JERSEYS_SELECTABLE = PERSON_JERSEYS.filter(j => !j.hidden)

// Kit squadra (dim_team.kit: {design, colors:{main, accent, accent2?, trim}, grad?}) -> campi dell'avatar persona.
// design: classic=3, vertical=6, horizontal=2, diagonal=4, gradient=7 (main alto, accent fondo, accent2 maniche, grad 'low'|'mid'), halves=8 (main sinistra, accent destra);
// "missing" (trame non disegnate: patchwork, grafiche) ripiega sulla maglia classica. accent2 su classic = manica destra diversa dalla sinistra.
const KIT_DESIGN = { classic: 3, vertical: 6, horizontal: 2, diagonal: 4, gradient: 7, halves: 8 }
export function personFromKit(kit) {
  const c = kit?.colors ?? {}
  const jersey = KIT_DESIGN[kit?.design] ?? 3
  const out = { jersey, main: c.main ?? PERSON_DEFAULT.main, accent: c.accent ?? c.main ?? PERSON_DEFAULT.accent, trim: c.trim ?? c.main ?? PERSON_DEFAULT.trim }
  if (c.accent2 && (jersey === 3 || jersey === 7)) out.accent2 = c.accent2
  if (jersey === 7) out.grad = kit.grad === 'low' ? 'low' : 'mid'
  return out
}

// Fino a questa dimensione (px dell'avatar intero) si usano i livelli da 192 px, sopra quelli da 512 (il telefono li rimpicciolisce).
// 72 copre tutti i corridori nell'app (busto da 52 = 68 px) e le righe di classifica e leghe. Non c'e' piu' la versione da 384 (2026-11-02).
export const TINY_AVATAR_MAX = 72

export const PERSON_DEFAULT = {
  gender: 'man',
  style: 'h1',
  headwear: 'none',
  glasses: false,
  jersey: 3,
  skin: '#EDC7A2',
  hair: '#3B2A20',     // colora capelli e sopracciglia (per il rasato: solo sopracciglia)
  iris: '#5A3B26',
  main: '#E8397D',     // rosa Giro (brand di default)
  accent: '#E8397D',   // = corpo: la maglia classica nasce tinta unita
  trim: '#111111',
  wear: '#FFFFFF',
  detail: '#4A4A4A',   // visiera del cappellino / prese d'aria del casco
  frame: '#111111',
  lens: '#3B5A78',     // lente: colore unico, la sfumatura diagonale e' lo stesso colore schiarito
}

// ─── Palette ──────────────────────────────────────────────────────────
// Toni naturali per pelle, capelli e occhi; una sola palette "da squadra" per tutto il resto (maglia, copricapo, occhiali).
export const PERSON_PALETTES = {
  skin: ['#F6DFCB', '#EDC7A2', '#E0AC80', '#C68A5E', '#A26640', '#7A4B32', '#5A3828', '#43281D'],
  hair: ['#33333A', '#3B2A20', '#5A3B26', '#7B5535', '#A98455', '#D3B074', '#E6D3A3', '#A8481E', '#9A9A98', '#D8D6D0'],
  iris: ['#3B271C', '#5A3B26', '#8A6A3B', '#4F7A4A', '#5B8FC7', '#7F8F9B'],
  team: [
    '#C8102E', '#7A1230', '#FF5500', '#F5C400', '#0F7B4A', '#0B4F33', '#0E8C93', '#3A8FD9',
    '#1D3F8F', '#12244D', '#5B2A86', '#E0557E', '#6B7280', '#111111', '#F5F5F5', '#C9A227',
  ],
}

// campo colore → palette
export const PERSON_COLOR_PALETTE = {
  skin: 'skin', hair: 'hair', iris: 'iris',
  main: 'team', accent: 'team', accent2: 'team', trim: 'team', wear: 'team', detail: 'team', frame: 'team', lens: 'team',
}

// Colore leggibile (nero/bianco) da usare sopra un colore, per il segno di spunta
export function onColor(hex) {
  const n = parseInt(hex.slice(1), 16)
  const l = (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255
  return l > 0.6 ? '#111111' : '#FFFFFF'
}

// Labbra: mix pelle + rosa scuro, naturale su ogni tono
export function lipColor(skinHex, mix = 0.45) {
  const n = parseInt(skinHex.slice(1), 16)
  const mixc = (a, b) => Math.round(a * (1 - mix) + b * mix)
  const r = mixc((n >> 16) & 255, 200), g = mixc((n >> 8) & 255, 90), b = mixc(n & 255, 95)
  return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`
}

export function darken(hex, f = 0.6) {
  const n = parseInt(hex.slice(1), 16)
  const c = (v) => Math.round(v * f)
  return `#${((1 << 24) | (c((n >> 16) & 255) << 16) | (c((n >> 8) & 255) << 8) | c(n & 255)).toString(16).slice(1)}`
}

// Mescola con il bianco (f = quota di bianco): la sfumatura della lente e' il colore scelto schiarito
export function lighten(hex, f = 0.6) {
  const n = parseInt(hex.slice(1), 16)
  const c = (v) => Math.round(v + (255 - v) * f)
  return `#${((1 << 24) | (c((n >> 16) & 255) << 16) | (c((n >> 8) & 255) << 8) | c(n & 255)).toString(16).slice(1)}`
}

// Colori fissi della maglia iridata (blu, rosso, nero, giallo, verde, dall'alto in basso): per il badge sul volto
export const RAINBOW_COLORS = ['#0085C7', '#DF0024', '#000000', '#F4C300', '#009F3D']
// Badge sul volto per ogni maglia speciale (dall'alto in basso); star = stella gialla al centro (campione europeo)
export const SPECIAL_BADGES = {
  world_champion:    { colors: RAINBOW_COLORS },
  european_champion: { colors: ['#4A9BD9', '#2B6CB8', '#1B3F94', '#12286E'], star: '#F4C300' },
  worldcup_leader:   { colors: ['#4A9664', '#4682DC', '#502882', '#CD282D', '#F0821C', '#F7E13C'], vertical: true },
}
